import {
  ConnectionAbortedError,
  ConnectionError,
  ConnectionTimeoutError,
  HttpError,
  HttpMethod,
  HttpRateLimitError,
  HttpResponseError
} from '@/api/error'
import $ from 'jquery'

interface RequestContext {
  url: string
  method: HttpMethod
  data?: { [key: string]: unknown }
  timeoutMs?: number
  headers?: { [key: string]: unknown }
}

interface ResponseContext extends RequestContext {
  status: number
  responseText: string | null
}

function jQueryErrorToHttpError(
  jqXHR: JQuery.jqXHR,
  textStatus: string,
  requestContext: RequestContext
): HttpError | Error {
  // Although we expect jqXHR to be defined, this works around and adds debug info for an unexpected scenario
  // observed in production.
  if (!jqXHR) {
    return new Error('jQuery AJAX error without jqXHR defined', {
      cause: {
        textStatus,
        requestContext
      }
    })
  }

  const responseContext: ResponseContext = {
    ...requestContext,
    status: jqXHR.status,
    responseText: null
  }

  try {
    responseContext.responseText = jqXHR.responseText || null
  } catch (e) {
    // Ignore `InvalidStateError`, thrown when `responseType` neither an empty string nor 'text'
  }
  if (textStatus === 'timeout') {
    return new ConnectionTimeoutError(responseContext)
  } else if (textStatus === 'abort') {
    return new ConnectionAbortedError(responseContext)
  } else if (jqXHR.status === 429) {
    // If the reset failed due to Cloudflare Rate Limiting, indicate how long to wait before retrying
    return new HttpRateLimitError({
      retryAfter: Number(jqXHR.getResponseHeader('Retry-After')),
      ...responseContext
    })
  } else if (!jqXHR.status) {
    return new ConnectionError(responseContext)
  } else {
    return new HttpResponseError(responseContext)
  }
}

export const jQueryAjaxToPromise = <T>(
  jQueryAjaxResult: JQuery.jqXHR<T>
): Promise<T> => {
  return new Promise((resolve, reject) => {
    return jQueryAjaxResult.then(resolve).fail(function (
      jqXHR: JQuery.jqXHR | Error,
      textStatus: string
    ) {
      // TODO: If this works reliably, consider consolidating jQueryAjaxCall logic to reuse this
      // @ts-expect-error: Context from jQuery.ajax() will contain request details
      const requestContext = this as { [key: string]: unknown }
      if (jqXHR instanceof Error) {
        reject(jqXHR)
      } else {
        reject(
          jQueryErrorToHttpError(jqXHR, textStatus, {
            url: requestContext.url as string,
            method: requestContext.type as HttpMethod,
            data: requestContext.data as { [key: string]: unknown },
            timeoutMs: requestContext.timeout as number,
            headers: requestContext.headers as
              | { [key: string]: unknown }
              | undefined
          })
        )
      }
    })
  })
}

export const jQueryAjaxCall = <T>({
  url,
  dataType,
  type,
  data,
  headers,
  timeout,
  authenticate
}: JQuery.AjaxSettings): Promise<T> => {
  return new Promise((resolve, reject) => {
    return $.ajax({
      url,
      type,
      data,
      headers,
      timeout,
      dataType,
      authenticate
    } as JQuery.AjaxSettings)
      .then(resolve)
      .fail((jqXHR: JQuery.jqXHR | Error, textStatus: string) => {
        if (jqXHR instanceof Error) {
          reject(jqXHR)
        } else {
          reject(
            jQueryErrorToHttpError(jqXHR, textStatus, {
              url: url as string,
              method: type as HttpMethod,
              data: data as { [key: string]: unknown },
              timeoutMs: timeout as number,
              headers
            })
          )
        }
      })
  })
}
